<template>
  <div ref="container" class="tag-list">
    <span
      v-for="tag in visibleTags"
      :key="tag"
      class="gs-tag"
    >
      {{ tag }}
    </span>

    <tag-overflow-popover
      v-if="overflowTags.length"
      ref="overflowTrigger"
      :tags="overflowTags"
      :slice-offset="0"
    />
  </div>
</template>

<script>
import TagOverflowPopover from '@/js/shared/TagOverflowPopover.vue';

export default {
  name: 'TagList',

  components: {
    TagOverflowPopover,
  },

  props: {
    tags: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      visibleTags: [],
      overflowTags: [],
    };
  },

  watch: {
    tags: {
      immediate: true,
      handler() {
        this.$nextTick(this.measureAndFit);
      },
    },
  },

  mounted() {
    this.$nextTick(this.measureAndFit);

    this.resizeObserver = new ResizeObserver(() => {
      this.measureAndFit();
    });

    if (this.$refs.container) {
      this.resizeObserver.observe(this.$refs.container);
    }
  },

  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },

  methods: {
    measureAndFit() {
      if (!this.tags || !this.tags.length) {
        this.visibleTags = [];
        this.overflowTags = [];
        return;
      }

      const { container } = this.$refs;
      if (!container) {
        this.visibleTags = [...this.tags];
        this.overflowTags = [];
        return;
      }

      // Render all tags first so the DOM is populated
      this.visibleTags = [...this.tags];
      this.overflowTags = [];

      this.$nextTick(() => {
        const { children } = container;

        if (!children || !children.length) {
          return;
        }

        // Collect widths of all tag elements (exclude overflow trigger if present)
        const tagElements = [];
        for (let i = 0; i < children.length; i += 1) {
          const el = children[i];
          if (el.classList.contains('gs-tag') && !el.classList.contains('gs-tag--overflow')) {
            tagElements.push(el);
          }
        }

        if (!tagElements.length) {
          return;
        }

        const gapWidth = 4;
        const containerWidth = container.offsetWidth;

        // First check if all tags fit without overflow
        let fitCount = 0;
        let usedWidth = 0;

        for (let i = 0; i < tagElements.length; i += 1) {
          const tagWidth = tagElements[i].offsetWidth;
          const needed = tagWidth + (fitCount > 0 ? gapWidth : 0);

          if (usedWidth + needed <= containerWidth) {
            usedWidth += needed;
            fitCount += 1;
          } else {
            break;
          }
        }

        // If all tags fit, no overflow needed
        if (fitCount >= this.tags.length) {
          this.visibleTags = [...this.tags];
          this.overflowTags = [];
          return;
        }

        // Overflow detected — calculate "+N" width and recompute
        const digitCount = String(this.tags.length).length;
        const overflowWidth = Math.max(28, digitCount * 7 + 16);
        const availableWidth = containerWidth - overflowWidth;

        // Recompute how many tags fit with overflow reserved
        fitCount = 0;
        usedWidth = 0;

        for (let i = 0; i < tagElements.length; i += 1) {
          const tagWidth = tagElements[i].offsetWidth;
          const needed = tagWidth + (fitCount > 0 ? gapWidth : 0);

          if (usedWidth + needed <= availableWidth) {
            usedWidth += needed;
            fitCount += 1;
          } else {
            break;
          }
        }

        // Ensure at least 1 tag always shows
        if (fitCount === 0 && this.tags.length > 0) {
          fitCount = 1;
        }

        this.visibleTags = this.tags.slice(0, fitCount);
        this.overflowTags = this.tags.slice(fitCount);
      });
    },
  },
};
</script>

<style scoped>
.tag-list {
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
  white-space: nowrap;
}

.gs-tag {
  display: flex;
  padding: 2px 3px;
  justify-content: center;
  align-items: center;
  border-radius: 9999px;
  border: none;
  min-height: unset;
  min-width: 0;
  background: #444444;
  color: #A5A5A5;
  font-family: Roboto, sans-serif;
  font-size: 0.75em;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  white-space: nowrap;
  cursor: default;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
}

.tag-list :deep(.tag--overflow) {
  display: flex;
  padding: 2px 3px;
  justify-content: center;
  align-items: center;
  border-radius: 9999px;
  border: none;
  min-height: unset;
  background: #444444;
  color: #A5A5A5;
  font-family: Roboto, sans-serif;
  font-size: 0.75em;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  white-space: nowrap;
  cursor: default;
}
</style>
